import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table, Dropdown } from "react-bootstrap";
import "../License/License.css";
import "../../Dashboard/Dashboard.css";
import "../../../../../common/styles/table.css";
import TablePagination from "../../../TeacherAdmin/pagination/TablePagination";
import { useGetPayableInvoices } from "../../../../../hooks/school/billing/useGetPayableInvoices";
import TableSkeleton from "../../../common/TableSkeleton";
import DeleteModal from "../../../../common/DeleteModal";
import { useCancleAddonInvoiceRequest } from "../../../../../hooks/licenses/useCancleAddonInvoiceRequest";
import { ITEMS_PER_PAGE } from "../../../../../constant/constant";

/**
 * @typedef {"school" | "organization"} TenantType
 */

/**
 * Invoice List Component (Payable Invoices)
 * @param {{ type?: TenantType }} props
 */

const Invoice = ({ type = "school" }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelInvoiceId, setCancelInvoiceId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const navigation = useNavigate();

  const { invoices, meta, loading, error, getPayableInvoices } =
    useGetPayableInvoices();

  const {
    cancleInvoice,
    loading: cancelLoading,
    error: cancelError,
    success: cancelSuccess,
  } = useCancleAddonInvoiceRequest();

  useEffect(() => {
    getPayableInvoices({
      page: currentPage,
      per_page: 10,
    });
  }, [currentPage]);

  const totalPages = meta?.last_page || 1;

  const handlePayOffline = (invoiceId) => {
    if (type === "school") {
      navigation(
        "/school-admin/purchase-licenses/manual-payment-form/" + invoiceId,
      );
    } else {
      navigation(
        "/organization/purchase-licenses/manual-payment-form/" + invoiceId,
      );
    }
  };

  const handlePayOnline = (invoiceId) => {
    if (type === "organization") {
      navigation(`/organization/${invoiceId}/payment`);
    } else {
      navigation(`/school-admin/${invoiceId}/payment`);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    console.log("View invoice clicked:", invoiceId);
    // TODO: open invoice pdf
  };

  // Cancel invoice modal handlers
  const handleShowCancel = (invoiceId) => {
    setCancelInvoiceId(invoiceId);
    setIsProcessing(false);
    setShowCancelModal(true);
  };

  const handleCloseCancel = () => {
    setShowCancelModal(false);
    setCancelInvoiceId(null);
    setIsProcessing(false);
  };

  const handleCancelInvoice = async () => {
    setIsProcessing(true);
    await cancleInvoice(cancelInvoiceId);
  };

  const handleCancelSuccess = () => {
    setShowCancelModal(false);
    setCancelInvoiceId(null);
    setIsProcessing(false);
    // Refresh the invoice list
    getPayableInvoices({
      page: currentPage,
      per_page: 10,
    });
  };

  // Only show states for the active cancel operation
  const showCancelLoading = isProcessing && cancelLoading;
  const showCancelError = isProcessing && cancelError;
  const showCancelSuccess = isProcessing && cancelSuccess;

  if (loading) {
    return (
      <div>
        <div className="license-header">
          <h1 className="license-title">All Invoices</h1>
        </div>
        <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="license-header">
        <h1 className="license-title">All Invoices</h1>
      </div>

      {/* Table */}
      <div className="custom-table-container mt-3">
        <div className="custom-table-wrapper">
          <Table hover className="custom-table">
            <thead className="custom-table-head">
              <tr>
                <th className="invoice-col-date text-nowrap">Date</th>
                <th className="invoice-col-no text-nowrap">Invoice No.</th>
                <th className="invoice-col-action text-nowrap">Action</th>
                <th className="invoice-col-amount text-nowrap">Total Amount</th>
                <th className="invoice-col-amount text-nowrap">Paid Amount</th>
                <th className="invoice-col-amount text-nowrap">Remaining Amount</th>
                <th className="invoice-col-status text-nowrap text-center">Status</th>
              </tr>
            </thead>

            <tbody>
              {!loading && error && (
                <tr>
                  <td colSpan={7} className="text-center text-danger py-4">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && invoices?.length > 0
                ? invoices.map((invoice) => (
                  <tr key={invoice?.id}>
                    <td className="invoice-col-date text-nowrap">
                      {new Date(invoice?.issued_at).toLocaleDateString(
                        "en-IN",
                      )}
                    </td>
                    <td className="invoice-col-no text-nowrap">
                      <Link to={`${type === "school" ? "/school-admin" : "/organization"}/settings/payments`}>
                        {invoice?.invoice_no}
                      </Link>
                    </td>

                    <td className="invoice-col-action text-nowrap">
                      {invoice?.can_pay ? (
                        <Dropdown>
                          <Dropdown.Toggle
                            as="button"
                            bsPrefix="dropdown-toggle-no-caret"
                            className="invoice-pay-btn"
                            style={{
                              background: "#0d6efd",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              fontWeight: "500",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <i className="fa-solid fa-wallet"></i>
                            Pay Now
                            <i className="fa-solid fa-chevron-down" style={{ fontSize: "10px" }}></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            align="end"
                            renderOnMount
                            popperConfig={{ strategy: "fixed" }}
                          >
                            <Dropdown.Item
                              onClick={() => handlePayOnline(invoice?.id)}
                            >
                              <i
                                className="fa-solid fa-credit-card me-2"
                                style={{ color: "#0d6efd" }}
                              ></i>
                              Pay Online
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handlePayOffline(invoice?.id)}
                            >
                              <i
                                className="fa-solid fa-money-bill me-2"
                                style={{ color: "#198754" }}
                              ></i>
                              Pay Offline
                            </Dropdown.Item>
                            {invoice?.status === "issued" && invoice?.is_addon && (
                              <Dropdown.Item
                                className="text-danger"
                                onClick={() => handleShowCancel(invoice?.id)}
                              >
                                <i className="fa-solid fa-ban me-2"></i>
                                Cancel Invoice
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : (
                        <button
                          className="view-receipt-link"
                          style={{ border: "none", background: "none", whiteSpace: "nowrap" }}
                          onClick={() => handleViewInvoice(invoice?.id)}
                        >
                          View Invoice
                        </button>
                      )}
                    </td>

                    <td className="invoice-col-amount text-nowrap">
                      {Number(invoice?.total_amount).toLocaleString()} (
                      {invoice?.currency})
                    </td>
                    <td className="invoice-col-amount text-nowrap">
                      {Number(invoice?.paid_amount).toLocaleString()} (
                      {invoice?.currency})
                    </td>
                    <td className="invoice-col-amount text-nowrap">
                      {Number(invoice?.due_amount).toLocaleString()} (
                      {invoice?.currency})
                    </td>

                    <td className="invoice-col-status text-center text-nowrap">
                      <span
                        className={
                          invoice?.status === "paid"
                            ? "status-success"
                            : "status-pending"
                        }
                      >
                        {invoice?.status}
                      </span>
                    </td>
                  </tr>
                ))
                : !loading && (
                  <tr>
                    <td colSpan={7}>
                      <div className="text-center m-5">
                        No invoices found.
                      </div>
                    </td>
                  </tr>
                )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        <TablePagination
          totalPage={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          disabled={loading}
        />
      </div>

      {/* Cancel Invoice Confirmation Modal */}
      <DeleteModal
        showModal={showCancelModal}
        handleClose={handleCloseCancel}
        message="Are you sure you want to cancel this invoice? This action cannot be undone."
        loading={showCancelLoading}
        handleDelete={handleCancelInvoice}
        error={showCancelError ? cancelError : ""}
        success={showCancelSuccess}
        successMessage="Invoice has been cancelled successfully."
        onSuccessContinue={handleCancelSuccess}
        customConfirmTitle="Cancel Invoice"
        customSuccessTitle="Invoice Cancelled"
        customErrorTitle="Cancellation Failed"
      />
    </div>
  );
};

export default Invoice;
